import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Clock } from 'lucide-react';
import { useModal, useStep } from '@/hooks';
import {
	buildSchedulingPreviewSession,
	decodeUrl,
	encodeUrl,
	formatProfessionalFullName,
	formatDatePickerLabelFromInstant,
	formatTimeSlot,
	isoDateKeyFromInstant,
	warmPaymentCheckoutCache,
} from '@/utils';
import {
	StepButtonContainer,
	ServiceModalityFeedback,
	TimeSlotSelect,
	SessionStepTitle,
} from '@/components';

import {
	useGetMyClub,
	useGetProfessionalPublic,
	useGetPublicAvailableDate,
	useGetSessions,
} from '@/services';

import { Avatar, AvailableDatePicker } from '@/components/ui';
import { getUserData } from '@/utils/cookies';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PaymentContainer } from '@/pages/user/components/PaymentContainer';
import { E2E_SCHEDULING } from '@/constants/e2eSchedulingTestIds';

interface StepScheduleUserProps {
	isPublicView?: boolean;
}

const StepScheduleUser = ({
	isPublicView: _isPublicView,
}: StepScheduleUserProps) => {
	const confirmButtonRef = useRef<HTMLDivElement>(null);
	const timeSelectionRef = useRef<HTMLDivElement>(null);
	const { previousStep, setCurrentStep, currentStep } = useStep();
	const {
		openModal,
		closeAllModals,
		setConfirmText,
		setCancelText,
		setOnConfirm,
	} = useModal();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [selectedDatePicker, setSelectedDatePicker] = useState({
		date: '',
		time: '',
	});
	const [availableDateDataPicker, setAvailableDateDataPicker] =
		useState<Array<string> | null>(null);
	const [availabelSlotsTime, setAvailabelSlotsTime] =
		useState<Array<AvailableDates.SessionTimeSlot> | null>(null);

	const [availableDateId, setAvailableDateId] = useState('');
	const [selectedDate, setSelectedDate] = useState('');

	const [selectedModality, setSelectedModality] =
		useState<Professionals.ServiceModality>('FULL_REMOTE');
	/** start_time (ISO) já aplicado a partir do param `t`, para não reaplicar a cada refetch. */
	const appliedPreselectRef = useRef<string | null>(null);
	/** Slot vindo do painel do profissional, para o seletor de horários já abrir marcado. */
	const [preselectedSlot, setPreselectedSlot] =
		useState<AvailableDates.SessionTimeSlot | null>(null);

	const encodedParams = searchParams.get('d');
	const decodedParams = useMemo(() => {
		if (!encodedParams) {
			return { s: '2', p: '' };
		}
		try {
			return decodeUrl<{ s: string; p: string; t?: string }>(
				encodedParams,
			);
		} catch {
			return { s: '2', p: '' };
		}
	}, [encodedParams]);

	const { data: professionalData } = useGetProfessionalPublic(
		decodedParams.p || '',
		{
			enabled: !!decodedParams.p,
		},
	);

	const professionalFullName = professionalData
		? formatProfessionalFullName(professionalData)
		: '';

	const { data: availableData, isFetching: isFetchingAvailableData } =
		useGetPublicAvailableDate(professionalData?.id || '', {
			enabled: !!professionalData?.id,
		});

	const professionalId = professionalData?.id ?? decodedParams.p ?? '';
	const checkoutServiceId =
		professionalData?.professional_services?.[0]?.service?.id ?? '';
	const isLoggedIn = Boolean(getUserData());

	useGetMyClub({
		enabled: !!professionalId && currentStep === 2 && isLoggedIn,
		staleTime: 60_000,
	});
	useGetSessions(
		{ isMe: true, limit: 1, page: 1 },
		{
			enabled: !!professionalId && currentStep === 2 && isLoggedIn,
			staleTime: 60_000,
		},
	);

	useEffect(() => {
		if (currentStep !== 2 || !professionalId || !isLoggedIn) return;
		warmPaymentCheckoutCache(queryClient, {
			professionalId,
			serviceId: checkoutServiceId,
			sessionId: '',
		});
	}, [
		checkoutServiceId,
		currentStep,
		isLoggedIn,
		professionalId,
		queryClient,
	]);

	const goToSchedulingSuccess = useCallback(() => {
		const encodedParam = encodeUrl({ s: 4 });
		searchParams.set('d', encodedParam);
		navigate(`?${searchParams.toString()}`, { replace: true });
		setCurrentStep(4);
	}, [navigate, searchParams, setCurrentStep]);

	const openSchedulingReviewModal = useCallback(
		(requestBody: Sessions.Create) => {
			const professionalIdForReturn =
				requestBody.professional_id ??
				professionalData?.id ??
				decodedParams.p ??
				'';
			const previewSession = buildSchedulingPreviewSession(
				requestBody,
				professionalData as Professionals.Professional | undefined,
			);

			warmPaymentCheckoutCache(
				queryClient,
				{
					professionalId: professionalIdForReturn,
					serviceId: requestBody.service_id,
					sessionId: '',
				},
				{ includeUserScoped: isLoggedIn },
			);

			openModal({
				size: 'wide',
				mobileSheet: 'tall',
				title: <SessionStepTitle />,
				content: (
					<div
						className="flex flex-col gap-4"
						data-testid={E2E_SCHEDULING.PAYMENT_ROOT}
					>
						<PaymentContainer
							session={previewSession}
							sessionCreatePayload={requestBody}
							embeddedInModal
							navigate={navigate}
							schedulingCheckoutReturn={
								professionalIdForReturn
									? {
											professionalId:
												professionalIdForReturn,
											sessionId: '',
									  }
									: undefined
							}
							onSessionCreated={(created) => {
								const encodedParam = encodeUrl({
									s: 2,
									p: decodedParams.p,
									session: created.id,
								});
								searchParams.set('d', encodedParam);
								navigate(`?${searchParams.toString()}`, {
									replace: true,
								});
							}}
							onPaymentClick={closeAllModals}
							onPaymentSettled={() => {
								closeAllModals();
								goToSchedulingSuccess();
							}}
						/>
					</div>
				),
			});
			setConfirmText('');
			setCancelText('');
			setOnConfirm(() => {});
		},
		[
			closeAllModals,
			decodedParams.p,
			goToSchedulingSuccess,
			isLoggedIn,
			navigate,
			openModal,
			professionalData,
			queryClient,
			searchParams,
			setCancelText,
			setConfirmText,
			setOnConfirm,
		],
	);

	useEffect(() => {
		setSelectedDatePicker({ date: '', time: '' });
		setAvailabelSlotsTime(null);
		setAvailableDateId('');
		setSelectedDate('');
		setAvailableDateDataPicker(null);
		setPreselectedSlot(null);
		appliedPreselectRef.current = null;
	}, [decodedParams?.p]);

	useEffect(() => {
		if (!selectedDatePicker.date) return;

		const frameId = requestAnimationFrame(() => {
			timeSelectionRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		});

		return () => cancelAnimationFrame(frameId);
	}, [selectedDatePicker.date]);

	useEffect(() => {
		if (selectedDatePicker.time && confirmButtonRef.current) {
			confirmButtonRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
			});
		}
	}, [selectedDatePicker.time]);

	useEffect(() => {
		if (professionalData) {
			if (professionalData.service_modality === 'HYBRID') {
				setSelectedModality('FULL_REMOTE');
			} else {
				setSelectedModality(professionalData.service_modality);
			}
		}
	}, [professionalData]);

	useEffect(() => {
		const availabilityData =
			availableData?.professionalAvailability?.availability;

		if (availabilityData) {
			const MINIMUM_LEAD_TIME_MINUTES = 10;
			const now = new Date();
			const earliestBookingTime = new Date(
				now.getTime() + MINIMUM_LEAD_TIME_MINUTES * 60000,
			);

			const validSlots = availabilityData
				.flatMap((day) => day.hours)
				.filter(
					(slot) => new Date(slot.start_time) > earliestBookingTime,
				);

			const localDates = validSlots.map((slot) => {
				const dateObj = new Date(slot.start_time);
				const year = dateObj.getFullYear();
				const month = String(dateObj.getMonth() + 1).padStart(2, '0');
				const day = String(dateObj.getDate()).padStart(2, '0');
				return `${year}-${month}-${day}`;
			});

			setAvailableDateDataPicker(Array.from(new Set(localDates)));
		}
	}, [availableData]);

	const loadSlotsForDayKey = useCallback(
		(dayKey: string) => {
			const allHours =
				availableData?.professionalAvailability?.availability?.flatMap(
					(day) => day.hours,
				) || [];

			const slotsForDay = allHours.filter(
				(hour) => isoDateKeyFromInstant(hour.start_time) === dayKey,
			);

			slotsForDay.sort(
				(a, b) =>
					new Date(a.start_time).getTime() -
					new Date(b.start_time).getTime(),
			);

			const MINIMUM_LEAD_TIME_MINUTES = 10;
			const earliestBookingTime = new Date(
				Date.now() + MINIMUM_LEAD_TIME_MINUTES * 60000,
			);

			return slotsForDay.filter(
				(s) => new Date(s.start_time) > earliestBookingTime,
			);
		},
		[availableData],
	);

	// Horário vindo do painel "Conheça o profissional" (`t` no param `d`):
	// quando a disponibilidade carrega, seleciona o dia e o slot automaticamente.
	useEffect(() => {
		const preselect = decodedParams?.t;
		if (!preselect || appliedPreselectRef.current === preselect) return;
		const allHours =
			availableData?.professionalAvailability?.availability?.flatMap(
				(day) => day.hours,
			) ?? [];
		if (allHours.length === 0) return;
		appliedPreselectRef.current = preselect;
		const target = new Date(preselect).getTime();
		const slot = allHours.find(
			(hour) => new Date(hour.start_time).getTime() === target,
		);
		if (!slot) return;
		const dayKey = isoDateKeyFromInstant(slot.start_time);
		setAvailabelSlotsTime(loadSlotsForDayKey(dayKey));
		setPreselectedSlot(slot);
		setAvailableDateId(slot.available_date_id);
		setSelectedDate(slot.start_time);
		setSelectedDatePicker({
			date: formatDatePickerLabelFromInstant(slot.start_time),
			time: formatTimeSlot(slot.start_time, slot.end_time) ?? '',
		});
	}, [decodedParams?.t, availableData, loadSlotsForDayKey]);

	const handleNext = () => {
		if (!professionalData) {
			toast.error(
				'Ocorreu algum erro inesperado, por favor entre em contato com o suporte',
			);
			return;
		}

		const requestBody: Sessions.Create = {
			available_date_id: availableDateId,
			is_online: selectedModality === 'FULL_REMOTE',
			professional_id: professionalData.id,
			selected_date: selectedDate,
			service_id:
				professionalData?.professional_services?.[0]?.service?.id || '',
			type: 'DEFAULT',
		};

		openSchedulingReviewModal(requestBody);
	};

	const handleSelectedTimeChange = (time: string) => {
		const parsedScheduleObject = JSON.parse(time);
		const formattedTime = formatTimeSlot(
			parsedScheduleObject.start_time,
			parsedScheduleObject.end_time,
		);

		setAvailableDateId(parsedScheduleObject.available_date_id);
		setSelectedDate(parsedScheduleObject.start_time);

		if (formattedTime)
			setSelectedDatePicker((prev) => ({ ...prev, time: formattedTime }));
	};

	const onAvailableDatePicker = (date: string | null) => {
		// O calendário reemite o dia já selecionado (ex.: ao montar com defaultValue);
		// nesse caso não limpa o horário escolhido.
		if (date && date === selectedDatePicker.date) return;
		setPreselectedSlot(null);
		if (!date) {
			setAvailabelSlotsTime([]);
			setSelectedDatePicker({ date: '', time: '' });
			setSelectedDate('');
			setAvailableDateId('');
			return;
		}

		const splitDate = date.split('/');
		const formattedDate = `${splitDate[2]}-${splitDate[1]}-${splitDate[0]}`;

		setAvailabelSlotsTime(loadSlotsForDayKey(formattedDate));
		setSelectedDatePicker({ date, time: '' });
		setSelectedDate('');
		setAvailableDateId('');
	};

	const handlePreviusStep = () => {
		const professionalFromUrl =
			decodedParams?.p ?? professionalData?.id ?? '';

		if (!professionalFromUrl) {
			previousStep();
			return;
		}

		const newParams = new URLSearchParams(searchParams.toString());
		newParams.set('d', encodeUrl({ s: 1, p: professionalFromUrl }));
		navigate(`?${newParams.toString()}`, { replace: true });
	};

	return (
		<section className="flex w-full flex-col gap-6">
			{professionalData && (
				<div className="flex items-center gap-3 rounded-2xl border border-zinc-100 bg-white px-3 py-2.5 shadow-sm md:w-fit">
					<Avatar
						img_url={professionalData.profile_photo_url || ''}
						variant="circle"
						className="size-9"
					/>
					<div className="flex flex-col gap-0.5 md:flex-row md:items-baseline md:gap-1">
						<span className="text-xs font-medium uppercase tracking-[0.14em] text-zinc-500">
							Horários disponíveis de
						</span>
						<span className="text-sm font-semibold text-zinc-800 md:text-base">
							{professionalFullName}
						</span>
					</div>
				</div>
			)}
			<section className="flex w-full flex-col justify-center gap-8 md:flex-row md:gap-0">
				<div className="flex w-full flex-col items-center space-y-4">
					<SessionStepTitle
						icon={<CalendarDays className="w-5 text-dark" />}
						title="Selecione uma data:"
						className="w-fit"
					/>
					<AvailableDatePicker
						availableDates={availableDateDataPicker || []}
						onDateChange={(date) => onAvailableDatePicker(date)}
						defaultValue={selectedDatePicker.date || null}
						loading={isFetchingAvailableData}
						lockPastMonths
						maxMonthsAhead={1}
						autoFocusFirstAvailableMonth
					/>
				</div>
				<div className="mx-4 hidden border border-s md:block"></div>
				<div className="flex w-full max-w-full flex-col items-stretch gap-4">
					<div
						ref={timeSelectionRef}
						className="mx-auto w-full scroll-mt-24 space-y-4 md:max-w-full"
					>
						<SessionStepTitle
							icon={<Clock className="w-5 text-dark" />}
							title="Selecione um horário:"
							className="mx-auto w-fit"
						/>
						<TimeSlotSelect
							slots={availabelSlotsTime || []}
							onValueChange={handleSelectedTimeChange}
							disabled={!selectedDatePicker.date}
							defaultValue={preselectedSlot}
						/>
					</div>
					{professionalData && (
						<div className="w-full shrink-0">
							<ServiceModalityFeedback
								data={professionalData}
								address={professionalData.clinic?.address}
								onModalityChange={setSelectedModality}
								selectedModality={selectedModality}
							/>
						</div>
					)}
				</div>
			</section>
			<div
				ref={confirmButtonRef}
				className="mt-6 w-full shrink-0 border-t border-zinc-100 pt-5 sm:mt-8 sm:pt-6"
			>
				<StepButtonContainer
					onHandleNext={handleNext}
					onHandlePrevius={handlePreviusStep}
					disabledNextButton={
						!selectedDatePicker.date || !selectedDatePicker.time
					}
					nextStepText="Revisar"
				/>
			</div>
		</section>
	);
};

export { StepScheduleUser };
